import React, { useState, useEffect, useCallback } from 'react';
import { User, Calendar, Phone, Mail, Heart, Activity, ChevronDown, ChevronUp, History, Edit2, Save, X } from 'lucide-react';
import PatientHistoryModal from './PatientHistoryModal';
import { patientAPI } from '../../../api/patient';
import { appointmentAPI } from '../../../api/appointment';

// Vitals validation constants (matching backend schema)
const VITALS_VALIDATION = {
  heightCm: { min: 0, max: 300, label: 'Height' },
  weightKg: { min: 0, max: 1000, label: 'Weight' },
  bmi: { min: 0, max: 100, label: 'BMI' },
  bpSys: { min: 0, max: 300, label: 'BP Systolic' },
  bpDia: { min: 0, max: 200, label: 'BP Diastolic' },
  pulse: { min: 0, max: 300, label: 'Pulse' },
  temperature: { min: 0, max: 110, label: 'Temperature' },
  oxygenSaturation: { min: 0, max: 100, label: 'Oxygen Saturation' },
  respiration: { min: 0, max: 100, label: 'Respiration' },
  sugar: { min: 0, max: 1000, label: 'Sugar' },
  pft: { min: 0, max: 200, label: 'PFT' }
};

const CollapsiblePatientCard = ({ 
  selectedPatient, 
  patientData, 
  compact = false
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [patientVitals, setPatientVitals] = useState(null);
  const [loadingVitals, setLoadingVitals] = useState(false);
  const [vitalsFromAppointment, setVitalsFromAppointment] = useState(null);
  const [isEditingVitals, setIsEditingVitals] = useState(false);
  const [isEditingBasicInfo, setIsEditingBasicInfo] = useState(false);
  const [editedVitals, setEditedVitals] = useState({});
  const [editedBasicInfo, setEditedBasicInfo] = useState({});
  const [savingVitals, setSavingVitals] = useState(false);
  const [savingBasicInfo, setSavingBasicInfo] = useState(false);
  const [patient, setPatient] = useState(null);
  const [vitalsErrors, setVitalsErrors] = useState({});
  const [patientLastUpdated, setPatientLastUpdated] = useState(null);

  const getPatientId = useCallback((p) => p?._id || p?.id, []);

  const getPatientCacheKey = useCallback((id) => {
    if (!id) return null;
    return `patient_cache_${id}`;
  }, []);

  const readCachedPatient = useCallback(
    (id) => {
      const key = getPatientCacheKey(id);
      if (!key) return null;
      try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return null;
        if (!parsed.patient) return null;
        return parsed;
      } catch {
        return null;
      }
    },
    [getPatientCacheKey]
  );

  const writeCachedPatient = useCallback(
    (id, patientToCache) => {
      const key = getPatientCacheKey(id);
      if (!key || !patientToCache) return;
      try {
        const payload = {
          patient: patientToCache,
          cachedAt: Date.now()
        };
        localStorage.setItem(key, JSON.stringify(payload));
      } catch {
        // ignore storage failures
      }
    },
    [getPatientCacheKey]
  );

  const getComparableUpdatedAtMs = useCallback((p) => {
    const candidate = p?.updatedAt || p?.lastUpdatedAt || p?.modifiedAt;
    if (!candidate) return 0;
    const ms = Date.parse(candidate);
    return Number.isFinite(ms) ? ms : 0;
  }, []);

  // Derive identifiers used throughout the component (handle multiple shapes)
  const patientId =
    patientData?.id ||
    patientData?._id ||
    patientData?.patient?.id ||
    patientData?.patient?._id ||
    selectedPatient?.patient?.id ||
    selectedPatient?.patient?._id ||
    selectedPatient?.patientId ||
    (typeof selectedPatient?.patient === 'string' ? selectedPatient.patient : null) ||
    patient?._id ||
    patient?.id;
  const currentAppointmentId = selectedPatient?._id || selectedPatient?.id;

  // Initialize patient data from props - only if not recently updated locally
  useEffect(() => {
    const initialPatient = patientData || selectedPatient?.patient;
    const initialPatientId =
      (typeof initialPatient === 'string' ? initialPatient : getPatientId(initialPatient)) ||
      patientId ||
      getPatientId(patient);

    // If we only have an ID (or missing id fields), fetch a fresh patient
    if (!initialPatient && !initialPatientId) {
      console.log('⚠️ [useEffect - Patient Init] No initial patient data available');
      return;
    }

    if (!initialPatientId) {
      // We have patient data but cannot key it; just set it and bail out.
      if (initialPatient && typeof initialPatient === 'object') {
        console.log('⚠️ [useEffect - Patient Init] Patient has no id/_id; using prop patient without caching');
        setPatient(initialPatient);
      }
      return;
    }
    
    // Check localStorage for recent updates to this specific patient (persists across tab switches)
    const storageKey = `patient_last_updated_${initialPatientId}`;
    const storedTimestamp = localStorage.getItem(storageKey);
    const lastUpdateTime = storedTimestamp ? parseInt(storedTimestamp, 10) : 0;
    const timeSinceUpdate = Date.now() - lastUpdateTime;
    const bufferTime = 10000; // 10 second buffer

    // Prefer cached patient object (prevents stale props overwriting after tab switches/remounts)
    const cached = readCachedPatient(initialPatientId);
    const cachedPatient = cached?.patient;
    const cachedAt = cached?.cachedAt || 0;
    const propsUpdatedAt = getComparableUpdatedAtMs(initialPatient);

    if (cachedPatient && getPatientId(cachedPatient) === initialPatientId) {
      const shouldPreferCache = cachedAt > lastUpdateTime || cachedAt > propsUpdatedAt;
      if (shouldPreferCache) {
        console.log('✅ [useEffect - Patient Init] Using cached patient (newer than props)');
        setPatient({ ...(typeof initialPatient === 'object' ? initialPatient : {}), ...cachedPatient });
        return;
      }
    }

    console.log('🔄 [useEffect - Patient Init] Triggered', {
      hasPatientData: !!patientData,
      hasSelectedPatient: !!selectedPatient,
      patientId: initialPatientId,
      currentPatientState: patient,
      localStorageTimestamp: storedTimestamp,
      timeSinceUpdate: lastUpdateTime > 0 ? timeSinceUpdate : 'Never updated',
      withinBuffer: timeSinceUpdate <= bufferTime
    });

    console.log('🔄 [useEffect - Patient Init] Checking if should update', {
      timeSinceUpdate,
      bufferTime,
      willUpdate: timeSinceUpdate > bufferTime,
      initialPatientId,
      initialPatientName: `${initialPatient.firstName} ${initialPatient.lastName}`,
      currentPatientId: patient?._id || patient?.id,
      currentPatientName: patient ? `${patient.firstName} ${patient.lastName}` : 'None'
    });

    // Only update from props if enough time has passed since last save
    if (timeSinceUpdate > bufferTime) {
      console.log('✅ [useEffect - Patient Init] Updating patient state from props');
      if (initialPatient && typeof initialPatient === 'object') {
        setPatient(initialPatient);
        writeCachedPatient(initialPatientId, initialPatient);
      } else {
        // If initialPatient is just an id, fetch it
        const fetchById = async () => {
          try {
            const response = await patientAPI.getById(initialPatientId);
            if (response.success) {
              const freshPatient = response.data?.patient || response.data;
              setPatient(freshPatient);
              writeCachedPatient(initialPatientId, freshPatient);
            }
          } catch (error) {
            console.error('❌ [useEffect - Patient Init] Failed to fetch patient by id:', error);
          }
        };
        fetchById();
      }
      return;
    } else {
      console.log('⏸️ [useEffect - Patient Init] Skipping update - within buffer period (recent save detected)');
      // If we're within buffer but don't have patient state, fetch fresh data
      if (!patient) {
        console.log('🔄 [useEffect - Patient Init] No patient state but within buffer - fetching fresh data');
        const fetchFreshData = async () => {
          try {
            const response = await patientAPI.getById(initialPatientId);
            if (response.success) {
              const freshPatient = response.data?.patient || response.data;
              console.log('✅ [useEffect - Patient Init] Loaded fresh patient data');
              setPatient(freshPatient);
              writeCachedPatient(initialPatientId, freshPatient);
            }
          } catch (error) {
            console.error('❌ [useEffect - Patient Init] Failed to fetch fresh data:', error);
            setPatient(initialPatient); // Fallback to prop data
          }
        };
        fetchFreshData();
      }
    }
  }, [patientData, selectedPatient, patientId, getPatientId, getComparableUpdatedAtMs, readCachedPatient, writeCachedPatient]);  // Removed patientLastUpdated from dependencies

  const loadCurrentAppointmentVitals = useCallback(
    async (forceApi = false) => {
      if (!currentAppointmentId) {
        setPatientVitals(null);
        setVitalsFromAppointment(null);
        setLoadingVitals(false);
        return;
      }

      const appointmentVitals = selectedPatient?.vitals;
      const hasAppointmentVitals =
        appointmentVitals && typeof appointmentVitals === 'object' && Object.keys(appointmentVitals).length > 0;

      if (!forceApi && hasAppointmentVitals) {
        setPatientVitals(appointmentVitals);
        setVitalsFromAppointment(selectedPatient);
        setLoadingVitals(false);
        return;
      }

      setLoadingVitals(true);
      try {
        const response = await appointmentAPI.getById(currentAppointmentId);

        if (response?.success && response?.data) {
          const appointment = response.data;
          const fetchedVitals =
            appointment?.vitals && Object.keys(appointment.vitals).length > 0 ? appointment.vitals : null;

          setPatientVitals(fetchedVitals);
          setVitalsFromAppointment(appointment);
        } else {
          setPatientVitals(null);
          setVitalsFromAppointment(null);
        }
      } catch (error) {
        console.error('Failed to fetch current appointment vitals:', error);
        setPatientVitals(null);
        setVitalsFromAppointment(null);
      } finally {
        setLoadingVitals(false);
      }
    },
    [currentAppointmentId, selectedPatient]
  );

  useEffect(() => {
    loadCurrentAppointmentVitals();
  }, [loadCurrentAppointmentVitals]);

  // Add effect to reload vitals when selectedPatient.vitals changes
  useEffect(() => {
    if (selectedPatient?.vitals && !isEditingVitals) {
      console.log('🔄 [CollapsiblePatientCard] selectedPatient.vitals changed, updating display', selectedPatient.vitals);
      setPatientVitals(selectedPatient.vitals);
      setVitalsFromAppointment(selectedPatient);
    }
  }, [selectedPatient?.vitals, selectedPatient, isEditingVitals]);

  const handleEditVitals = () => {
    setEditedVitals(patientVitals || {});
    setIsEditingVitals(true);
  };

  const handleCancelEditVitals = () => {
    setEditedVitals({});
    setVitalsErrors({});
    setIsEditingVitals(false);
  };

  // Validate a single vital field
  const validateVitalField = (field, value) => {
    if (value === '' || value === null || value === undefined) {
      return null; // Empty values are allowed (optional fields)
    }

    const validation = VITALS_VALIDATION[field];
    if (!validation) return null;

    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      return `${validation.label} must be a valid number`;
    }

    if (numValue < validation.min || numValue > validation.max) {
      return `${validation.label} must be between ${validation.min}-${validation.max}`;
    }

    return null;
  };

  const handleSaveVitals = async () => {
    console.log('🔵 [handleSaveVitals] FUNCTION CALLED - Button clicked!');
    
    // Validate all vitals before saving
    const errors = {};
    Object.keys(editedVitals).forEach(field => {
      if (editedVitals[field] !== '' && editedVitals[field] !== null && editedVitals[field] !== undefined) {
        const error = validateVitalField(field, editedVitals[field]);
        if (error) {
          errors[field] = error;
        }
      }
    });

    if (Object.keys(errors).length > 0) {
      setVitalsErrors(errors);
      alert('Please fix validation errors before saving');
      return;
    }

    // Get appointment ID from current appointment or from vitals appointment
    const appointmentId = currentAppointmentId || vitalsFromAppointment?._id;

    console.log('🏥 [handleSaveVitals] Starting vitals save...', {
      selectedPatient,
      currentAppointmentId,
      vitalsFromAppointmentId: vitalsFromAppointment?._id,
      finalAppointmentId: appointmentId,
      editedVitals
    });

    if (!appointmentId) {
      // If no appointment exists, we can't save vitals yet
      // Store them locally and show success message
      console.warn('🏥 [handleSaveVitals] No appointment ID found, saving locally only');
      setPatientVitals(editedVitals);
      setIsEditingVitals(false);
      alert('No active appointment found. Vitals saved locally.');
      return;
    }

    setSavingVitals(true);
    try {
      // Prepare the update payload with only vitals (PATCH endpoint)
      const updatePayload = {
        vitals: {
          heightCm: editedVitals.heightCm || undefined,
          weightKg: editedVitals.weightKg || undefined,
          bmi: editedVitals.bmi || undefined,
          bpSys: editedVitals.bpSys || undefined,
          bpDia: editedVitals.bpDia || undefined,
          pulse: editedVitals.pulse || undefined,
          temperature: editedVitals.temperature || undefined,
          oxygenSaturation: editedVitals.oxygenSaturation || undefined,
          respiration: editedVitals.respiration || undefined,
          sugar: editedVitals.sugar || undefined,
          pft: editedVitals.pft || undefined
        }
      };

      // Remove undefined fields
      Object.keys(updatePayload.vitals).forEach(key => {
        if (updatePayload.vitals[key] === undefined) {
          delete updatePayload.vitals[key];
        }
      });

      console.log('🏥 [handleSaveVitals] Calling appointmentAPI.update with:', {
        appointmentId: appointmentId,
        payload: updatePayload
      });

      const response = await appointmentAPI.update(appointmentId, updatePayload);
      
      console.log('🏥 [handleSaveVitals] API response:', response);

      if (response.success) {
        console.log('✅ [handleSaveVitals] Vitals saved successfully, updating local state');
        // Update vitals from API response
        const updatedVitals = response.data?.vitals || editedVitals;
        setPatientVitals(updatedVitals);
        setVitalsFromAppointment({
          ...vitalsFromAppointment,
          vitals: updatedVitals,
          updatedAt: response.data?.updatedAt || new Date().toISOString()
        });
        setIsEditingVitals(false);
        
        console.log('✅ [handleSaveVitals] Save process complete');
      } else {
        throw new Error(response.message || 'Update failed');
      }
    } catch (error) {
      console.error('🏥 [handleSaveVitals] Failed to update vitals:', error);
      alert('Failed to update vitals. Please try again.');
    } finally {
      setSavingVitals(false);
    }
  };

  const handleVitalChange = (field, value) => {
    setEditedVitals(prev => {
      const updated = { ...prev, [field]: value };
      
      // Auto-calculate BMI if height and weight are provided
      if ((field === 'heightCm' || field === 'weightKg') && updated.heightCm && updated.weightKg) {
        const heightInMeters = updated.heightCm / 100;
        const bmi = (updated.weightKg / (heightInMeters * heightInMeters)).toFixed(1);
        updated.bmi = parseFloat(bmi);
      }
      
      return updated;
    });

    // Validate the field on change
    const error = validateVitalField(field, value);
    setVitalsErrors(prev => {
      const updated = { ...prev };
      if (error) {
        updated[field] = error;
      } else {
        delete updated[field];
      }
      return updated;
    });
  };

  const handleEditBasicInfo = () => {
    setEditedBasicInfo({
      firstName: patient?.firstName || '',
      lastName: patient?.lastName || '',
      mobile: patient?.mobile || '',
      email: patient?.email || '',
      sex: patient?.sex || patient?.gender || '',
      bloodGroup: patient?.bloodGroup || '',
      dob: patient?.dob ? new Date(patient.dob).toISOString().split('T')[0] : ''
    });
    setIsEditingBasicInfo(true);
  };

  const handleCancelEditBasicInfo = () => {
    setEditedBasicInfo({});
    setIsEditingBasicInfo(false);
  };

  const handleSaveBasicInfo = async () => {
    console.log('🔵 [handleSaveBasicInfo] FUNCTION CALLED - Button clicked!');
    
    if (!patientId) {
      alert('Patient ID not found');
      return;
    }

    console.log('💾 [handleSaveBasicInfo] Starting patient info save...', {
      patientId,
      editedBasicInfo
    });

    setSavingBasicInfo(true);
    try {
      console.log('💾 [handleSaveBasicInfo] Calling patientAPI.update');
      const response = await patientAPI.update(patientId, editedBasicInfo);
      
      console.log('💾 [handleSaveBasicInfo] API response:', response);
      
      if (response.success) {
        console.log('✅ [handleSaveBasicInfo] Patient info saved successfully');
        console.log('📊 [handleSaveBasicInfo] Response data:', response.data);
        
        // Fetch fresh patient data from API to ensure we have the latest
        try {
          console.log('🔄 [handleSaveBasicInfo] Fetching fresh patient data from API...');
          const freshPatientResponse = await patientAPI.getById(patientId);
          console.log('📊 [handleSaveBasicInfo] Fresh patient response:', freshPatientResponse);
          
          if (freshPatientResponse.success) {
            const freshPatient = freshPatientResponse.data?.patient || freshPatientResponse.data;
            console.log('✅ [handleSaveBasicInfo] Setting fresh patient data:', {
              id: freshPatient._id || freshPatient.id,
              firstName: freshPatient.firstName,
              lastName: freshPatient.lastName,
              mobile: freshPatient.mobile,
              email: freshPatient.email,
              fullData: freshPatient
            });
            setPatient(freshPatient);
            writeCachedPatient(patientId, freshPatient);
            console.log('✅ [handleSaveBasicInfo] Fresh patient data loaded and set');
          } else {
            console.warn('⚠️ [handleSaveBasicInfo] Fresh fetch failed, using response data as fallback');
            // Fallback to response data if fresh fetch fails
            const updatedPatient = response.data?.patient || response.data;
            const updatedData = {
              ...patient,
              ...updatedPatient,
              firstName: updatedPatient.firstName || editedBasicInfo.firstName,
              lastName: updatedPatient.lastName || editedBasicInfo.lastName,
              mobile: updatedPatient.mobile || editedBasicInfo.mobile,
              email: updatedPatient.email || editedBasicInfo.email,
              sex: updatedPatient.sex || editedBasicInfo.sex,
              gender: updatedPatient.gender || updatedPatient.sex || editedBasicInfo.sex,
              bloodGroup: updatedPatient.bloodGroup || editedBasicInfo.bloodGroup,
              dob: updatedPatient.dob || editedBasicInfo.dob,
              fullName: updatedPatient.fullName || `${updatedPatient.firstName || editedBasicInfo.firstName} ${updatedPatient.lastName || editedBasicInfo.lastName}`.trim()
            };
            setPatient(updatedData);
            writeCachedPatient(patientId, updatedData);
          }
        } catch (fetchError) {
          console.error('❌ [handleSaveBasicInfo] Failed to fetch fresh patient data:', fetchError);
          // Use response data as fallback
          const updatedPatient = response.data?.patient || response.data;
          const updatedData = {
            ...patient,
            ...updatedPatient,
            fullName: `${updatedPatient.firstName || editedBasicInfo.firstName} ${updatedPatient.lastName || editedBasicInfo.lastName}`.trim()
          };
          console.log('📊 [handleSaveBasicInfo] Using fallback data:', updatedData);
          setPatient(updatedData);
          writeCachedPatient(patientId, updatedData);
        }
        
        const timestamp = Date.now();
        const storageKey = `patient_last_updated_${patientId}`;
        console.log('⏰ [handleSaveBasicInfo] Setting timestamp:', timestamp);
        console.log('💾 [handleSaveBasicInfo] Storing timestamp in localStorage:', storageKey);
        
        // Store in both state and localStorage (localStorage persists across tab switches)
        setPatientLastUpdated(timestamp);
        localStorage.setItem(storageKey, timestamp.toString());
        
        setIsEditingBasicInfo(false);
        
        console.log('✅ [handleSaveBasicInfo] Save process complete');
      } else {
        throw new Error(response.message || 'Update failed');
      }
    } catch (error) {
      console.error('Failed to update patient info:', error);
      alert('Failed to update patient information. Please try again.');
    } finally {
      setSavingBasicInfo(false);
    }
  };

  const handleBasicInfoChange = (field, value) => {
    setEditedBasicInfo(prev => ({ ...prev, [field]: value }));
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return 'N/A';
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age; 
  };

  const formatGender = (gender) => {
    if (!gender) return 'N/A';
    return gender.charAt(0).toUpperCase() + gender.slice(1).toLowerCase();
  };

  if (!selectedPatient && !patientData) {
    return (
      <div className={`bg-white rounded-lg border-2 border-dashed border-gray-300 ${compact ? 'p-4' : 'p-6'}`}>
        <div className="text-center">
          <User className="mx-auto h-12 w-12 text-gray-400 mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Patient Selected</h3>
          <p className="text-gray-500 text-sm">Select a patient from the queue to view details</p>
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="bg-red-50 rounded-lg border-2 border-red-200 p-6">
        <div className="text-center">
          <h3 className="text-lg font-medium text-red-900 mb-2">No Patient Data</h3>
          <p className="text-red-600 text-sm">Patient information is not available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border-2 border-gray-200 overflow-hidden" style={{ fontFamily: 'Alata, Poppins, sans-serif' }}>
      {/* Header - Always Visible */}
      <div
        className="px-5 py-4 cursor-pointer hover:bg-gray-50 transition-colors border-b border-gray-100"
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Patient Photo with fallback to initials */}
            <div className="relative h-28 w-30 rounded-lg overflow-hidden border-2 border-[#2D869C] bg-[#9DC9D4]/20 flex items-center justify-center">
              {(selectedPatient?.patient?.photo?.url || patient?.photo?.url) ? (
                <img
                  src={selectedPatient?.patient?.photo?.url || patient?.photo?.url}
                  alt={`${patient?.firstName || 'Patient'} photo`}
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const fallback = e.currentTarget.parentElement.querySelector('[data-fallback-initials]');
                    if (fallback) fallback.classList.remove('hidden');
                  }}
                />
              ) : null}
              <div
                data-fallback-initials
                className={`${(selectedPatient?.patient?.photo?.url || patient?.photo?.url) ? 'hidden' : 'flex'} absolute inset-0 items-center justify-center bg-[#2D869C]`}
              >
                <span className="text-white font-bold text-4xl" style={{ fontFamily: 'Alata, Poppins, sans-serif' }}>
                  {patient?.firstName?.[0] || selectedPatient?.patient?.firstName?.[0] || 'U'}
                  {patient?.lastName?.[0] || selectedPatient?.patient?.lastName?.[0] || ''}
                </span>
              </div>
            </div>
            <div>
              <h2 style={{ fontFamily: 'Alata, Poppins, sans-serif' }} className="text-2xl font-bold text-black">
                {patient?.fullName || `${patient?.firstName} ${patient?.lastName}`}
              </h2>
              <div className="items-center gap-4 text-md text-black">
                <p className="text-black font-semibold">Mobile: <span className="font-medium text-black">{patient?.mobile || 'N/A'}</span></p>
                <p className="text-black font-semibold">Email &nbsp;: <span className="font-medium text-[#2D869C]">{patient?.email || 'N/A'}</span></p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-xl font-semibold text-black">
                {selectedPatient?.dateTime 
                  ? new Date(selectedPatient.dateTime).toLocaleDateString('en-IN', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric'
                    })
                  : new Date().toLocaleDateString('en-IN', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric'
                    })
                }
              </div>
              <div className="text-xl font-medium text-gray-600">
                {selectedPatient?.dateTime 
                  ? new Date(selectedPatient.dateTime).toLocaleTimeString('en-IN', {
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true
                    })
                  : new Date().toLocaleTimeString('en-IN', {
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true
                    })
                }
              </div>
            </div>

            {/* Collapse/Expand Button */}
            <div className="flex items-center">
              {isCollapsed ? (
                <ChevronDown className="h-5 w-5 text-[#2D869C]" />
              ) : (
                <ChevronUp className="h-5 w-5 text-[#2D869C]" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Collapsible Content */}
      {!isCollapsed && (
        <div className="px-5 pb-5">
          {/* Patient Information Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Basic Info */}
            <div className="bg-gray-50 rounded-lg p-4 border-2 border-gray-200 lg:col-span-1">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <User className="h-6 w-6 text-[#000000]" />
                  <h3 className="font-semibold text-gray-900 text-xl">Basic Info</h3>
                </div>
                {!isEditingBasicInfo ? (
                  <button
                    onClick={handleEditBasicInfo}
                    className="text-[#2D869C] hover:text-[#236b7a] p-1 rounded hover:bg-[#9DC9D4]/20 transition-colors"
                    title="Edit basic information"
                  >
                    <Edit2 className="h-6 w-6" />
                  </button>
                ) : (
                  <div className="flex gap-1">
                    <button
                      onClick={() => {
                        handleSaveBasicInfo();
                      }}
                      disabled={savingBasicInfo}
                      className="text-green-600 hover:text-green-800 p-1 rounded hover:bg-green-50 transition-colors disabled:opacity-50"
                      title="Save changes"
                    >
                      {savingBasicInfo ? (
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
                      ) : (  
                        <Save className="h-6 w-6" />
                      )}
                    </button>
                    <button
                      onClick={handleCancelEditBasicInfo}
                      disabled={savingBasicInfo}
                      className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50 transition-colors disabled:opacity-50"
                      title="Cancel editing"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>
                )}
              </div>
              <div className="space-y-2 text-base">
                {isEditingBasicInfo ? (
                  <>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">First Name:</span>
                      <input
                        type="text"
                        value={editedBasicInfo.firstName}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^a-zA-Z ]/g, '');
                          handleBasicInfoChange('firstName', value);
                        }}
                        className="font-medium border border-gray-300 rounded px-2 py-1 w-36 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Last Name:</span>
                      <input
                        type="text"
                        value={editedBasicInfo.lastName}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^a-zA-Z ]/g, '');
                          handleBasicInfoChange('lastName', value);
                        }}
                        className="font-medium border border-gray-300 rounded px-2 py-1 w-36 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Mobile:</span>
                      <input
                        type="text"
                        value={editedBasicInfo.mobile}
                        onChange={(e) => handleBasicInfoChange('mobile', e.target.value)}
                        className="font-medium border border-gray-300 rounded px-2 py-1 w-36 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Email:</span>
                      <input
                        type="email"
                        value={editedBasicInfo.email}
                        onChange={(e) => handleBasicInfoChange('email', e.target.value)}
                        className="font-medium border border-gray-300 rounded px-2 py-1 w-36 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Gender:</span>
                      <select
                        value={editedBasicInfo.sex}
                        onChange={(e) => handleBasicInfoChange('sex', e.target.value)}
                        className="font-medium border border-gray-300 rounded px-2 py-1 w-36 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Blood Group:</span>
                      <select
                        value={editedBasicInfo.bloodGroup}
                        onChange={(e) => handleBasicInfoChange('bloodGroup', e.target.value)}
                        className="font-medium border border-gray-300 rounded px-2 py-1 w-36 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select</option>
                        <option value="A+">A+</option>
                        <option value="A-">A-</option>
                        <option value="B+">B+</option>
                        <option value="B-">B-</option>
                        <option value="AB+">AB+</option>
                        <option value="AB-">AB-</option>
                        <option value="O+">O+</option>
                        <option value="O-">O-</option>
                      </select>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">DOB:</span>
                      <input
                        type="date"
                        value={editedBasicInfo.dob}
                        onChange={(e) => handleBasicInfoChange('dob', e.target.value)}
                        className="font-medium border border-gray-300 rounded px-2 py-1 w-36 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between items-center">
                      <span className="text-black font-semibold">Gender:</span>
                      <span className="text-black font-semibold">{formatGender(patient?.sex || patient?.gender)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-black font-semibold">Blood Group:</span>
                      <span className="text-black font-semibold  ">{patient?.bloodGroup || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-black font-semibold">DOB:</span>
                      <span className="text-black font-semibold ">{patient?.dob ? new Date(patient.dob).toLocaleDateString() : 'N/A'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-black font-semibold">Age:</span>
                      <span className="text-black font-semibold ">{String(patient?.age || calculateAge(patient?.dateOfBirth || patient?.dob))} years</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Vital Signs */}
            <div className="bg-gray-50 rounded-lg p-4 border-2 border-gray-200 lg:col-span-2">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-[#2D869C]" />
                  <h3 className="font-semibold text-gray-900 text-xl">Vital Signs</h3>
                  {loadingVitals && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#2D869C]"></div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {!isEditingVitals && !loadingVitals && patientVitals && (
                    <button
                      onClick={handleEditVitals}
                      className="text-[#2D869C] hover:text-[#236b7a] p-1 rounded hover:bg-[#9DC9D4]/20 transition-colors"
                      title="Edit vitals"
                    >
                      <Edit2 className="h-6 w-6" />
                    </button>
                  )}
                  {isEditingVitals && (
                    <div className="flex gap-1">
                      <button
                        onClick={() => {
                          console.log('🟢 SAVE VITALS BUTTON CLICKED!');
                          handleSaveVitals();
                        }}
                        disabled={savingVitals}
                        className="text-green-600 hover:text-green-800 p-1 rounded hover:bg-green-50 transition-colors disabled:opacity-50"
                        title="Save vitals"
                      >
                        {savingVitals ? (
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
                        ) : (
                          <Save className="h-6 w-6" />
                        )}
                      </button>
                      <button
                        onClick={handleCancelEditVitals}
                        disabled={savingVitals}
                        className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50 transition-colors disabled:opacity-50"
                        title="Cancel editing"
                      >
                        <X className="h-6 w-6" />
                      </button>
                    </div>
                  )}
                  {!loadingVitals && !isEditingVitals && currentAppointmentId && (
                    <button
                      onClick={() => loadCurrentAppointmentVitals(true)}
                      className="text-xl text-[#1f94b1] hover:text-[#236b7a] flex items-center gap-1"
                      title="Refresh vitals from latest appointment"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      <span className="text-xl">Refresh</span>
                    </button>
                  )}
                </div>
              </div>
              
              {/* Vitals content */}
              {loadingVitals ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mr-3"></div>
                  <span className="text-gray-500">Loading vitals...</span>
                </div>
              ) : patientVitals || isEditingVitals ? (
                <div className="space-y-2">
                  {vitalsFromAppointment && !isEditingVitals && (
                    <div className="mb-2 p-2 bg-blue-50 rounded text- text-blue-700">
                      <Calendar className="inline w-5 h-5 mr-1" />
                      {(() => {
                        const recordDate = new Date(vitalsFromAppointment.updatedAt || vitalsFromAppointment.dateTime || vitalsFromAppointment.createdAt);
                        const dateStr = recordDate.toLocaleDateString('en-IN');
                        const timeStr = recordDate.toLocaleTimeString('en-IN', { 
                          hour: '2-digit', 
                          minute: '2-digit',
                          hour12: true 
                        });
                        return `Recorded on: ${dateStr} ${timeStr}`;
                      })()}
                    </div>
                  )}
                 
                  {/* Two column grid for vitals */}
                  <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-base">
                    {isEditingVitals ? (
                      <>
                        <div className="flex flex-col">
                          <div className="flex justify-between items-center">
                            <span className="text-black font-semibold">Height (cm):</span>
                            <input
                              type="number"
                              step="0.1"
                              value={editedVitals.heightCm || ''}
                              onChange={(e) => handleVitalChange('heightCm', parseFloat(e.target.value) || '')}
                              className={`font-medium border ${vitalsErrors.heightCm ? 'border-red-500' : 'border-gray-300'} rounded px-2 py-1 w-24 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                              placeholder="170"
                            />
                          </div>
                          {vitalsErrors.heightCm && (
                            <span className="text-sm text-red-600 mt-1 ml-auto">{vitalsErrors.heightCm}</span>
                          )}
                        </div>
                        <div className="flex flex-col">
                          <div className="flex justify-between items-center">
                            <span className="text-black font-semibold">Weight (kg):</span>
                            <input
                              type="number"
                              step="0.1"
                              value={editedVitals.weightKg || ''}
                              onChange={(e) => handleVitalChange('weightKg', parseFloat(e.target.value) || '')}
                              className={`font-medium border ${vitalsErrors.weightKg ? 'border-red-500' : 'border-gray-300'} rounded px-2 py-1 w-24 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                              placeholder="65"
                            />
                          </div>
                          {vitalsErrors.weightKg && (
                            <span className="text-sm text-red-600 mt-1 ml-auto">{vitalsErrors.weightKg}</span>
                          )}
                        </div>
                        <div className="flex flex-col">
                          <div className="flex justify-between items-center">
                            <span className="text-black font-semibold">BMI:</span>
                            <input
                              type="number"
                              step="0.1"
                              value={editedVitals.bmi || ''}
                              readOnly
                              className="font-medium border border-gray-200 rounded px-2 py-1 w-24 text-sm bg-gray-50 cursor-not-allowed [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                              placeholder="Auto"
                            />
                          </div>
                          {vitalsErrors.bmi && (
                            <span className="text-sm text-red-600 mt-1 ml-auto">{vitalsErrors.bmi}</span>
                          )}
                        </div>
                        <div className="flex flex-col">
                          <div className="flex justify-between items-center">
                            <span className="text-black font-semibold">BP (Sys/Dia):</span>
                            <div className="flex space-x-1">
                              <input
                                type="number"
                                value={editedVitals.bpSys || ''}
                                onChange={(e) => handleVitalChange('bpSys', parseFloat(e.target.value) || '')}
                                className={`font-medium border ${vitalsErrors.bpSys ? 'border-red-500' : 'border-gray-300'} rounded px-2 py-1 w-14 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                                placeholder="120"
                              />
                              <span className="text-gray-400">/</span>
                              <input
                                type="number"
                                value={editedVitals.bpDia || ''}
                                onChange={(e) => handleVitalChange('bpDia', parseFloat(e.target.value) || '')}
                                className={`font-medium border ${vitalsErrors.bpDia ? 'border-red-500' : 'border-gray-300'} rounded px-2 py-1 w-14 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                                placeholder="80"
                              />
                            </div>
                          </div>
                          {(vitalsErrors.bpSys || vitalsErrors.bpDia) && (
                            <span className="text-sm text-red-600 mt-1 ml-auto">
                              {vitalsErrors.bpSys || vitalsErrors.bpDia}
                            </span>
                          )}
                        </div>
                        <div className="flex flex-col">
                          <div className="flex justify-between items-center">
                            <span className="text-black font-semibold">Pulse (bpm):</span>
                            <input
                              type="number"
                              value={editedVitals.pulse || ''}
                              onChange={(e) => handleVitalChange('pulse', parseFloat(e.target.value) || '')}
                              className={`font-medium border ${vitalsErrors.pulse ? 'border-red-500' : 'border-gray-300'} rounded px-2 py-1 w-24 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                              placeholder="72"
                            />
                          </div>
                          {vitalsErrors.pulse && (
                            <span className="text-sm text-red-600 mt-1 ml-auto">{vitalsErrors.pulse}</span>
                          )}
                        </div>
                        <div className="flex flex-col">
                          <div className="flex justify-between items-center">
                            <span className="text-black font-semibold">Temp (°F):</span>
                            <input
                              type="number"
                              step="0.1"
                              value={editedVitals.temperature || ''}
                              onChange={(e) => handleVitalChange('temperature', parseFloat(e.target.value) || '')}
                              className={`font-medium border ${vitalsErrors.temperature ? 'border-red-500' : 'border-gray-300'} rounded px-2 py-1 w-24 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                              placeholder="98.6"
                            />
                          </div>
                          {vitalsErrors.temperature && (
                            <span className="text-sm text-red-600 mt-1 ml-auto">{vitalsErrors.temperature}</span>
                          )}
                        </div>
                        <div className="flex flex-col">
                          <div className="flex justify-between items-center">
                            <span className="text-black font-semibold">SpO₂ (%):</span>
                            <input
                              type="number"
                              value={editedVitals.oxygenSaturation || ''}
                              onChange={(e) => handleVitalChange('oxygenSaturation', parseFloat(e.target.value) || '')}
                              className={`font-medium border ${vitalsErrors.oxygenSaturation ? 'border-red-500' : 'border-gray-300'} rounded px-2 py-1 w-24 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                              placeholder="98"
                            />
                          </div>
                          {vitalsErrors.oxygenSaturation && (
                            <span className="text-sm text-red-600 mt-1 ml-auto">{vitalsErrors.oxygenSaturation}</span>
                          )}
                        </div>
                        <div className="flex flex-col">
                          <div className="flex justify-between items-center">
                            <span className="text-black font-semibold">Respiration (rpm):</span>
                            <input
                              type="number"
                              value={editedVitals.respiration || ''}
                              onChange={(e) => handleVitalChange('respiration', parseFloat(e.target.value) || '')}
                              className={`font-medium border ${vitalsErrors.respiration ? 'border-red-500' : 'border-gray-300'} rounded px-2 py-1 w-24 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                              placeholder="16"
                            />
                          </div>
                          {vitalsErrors.respiration && (
                            <span className="text-sm text-red-600 mt-1 ml-auto">{vitalsErrors.respiration}</span>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex justify-between items-center">
                          <span className="text-black font-semibold">Height:</span>
                          <span className="text-black font-semibold">{patientVitals?.heightCm || 'N/A'} cm</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-black font-semibold">Weight:</span>
                          <span className="text-black font-semibold">{patientVitals?.weightKg || 'N/A'} kg</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-black font-semibold">BMI:</span>
                          <span className={`text-black font-semibold ${
                            patientVitals?.bmi > 25 ? 'text-orange-600' : 
                            patientVitals?.bmi < 18.5 ? 'text-blue-600' : 'text-green-600'
                          }`}>
                            {patientVitals?.bmi || 'N/A'}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-black font-semibold">BP:</span>
                          <span className="text-red-600 font-semibold">
                            {patientVitals?.bpSys || 'N/A'}/{patientVitals?.bpDia || 'N/A'}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-black font-semibold">Pulse:</span>
                          <span className="text-black font-semibold">{patientVitals?.pulse || 'N/A'} bpm</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-black font-semibold">Temperature:</span>
                          <span className="text-black font-semibold">{patientVitals?.temperature || 'N/A'}°F</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-black font-semibold">SpO₂:</span>
                          <span className="text-black font-semibold">{patientVitals?.oxygenSaturation || 'N/A'}%</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-black font-semibold">Respiration:</span>
                          <span className="text-black font-semibold">{patientVitals?.respiration || 'N/A'} rpm</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-gray-500 space-y-3">
                  <p>No vitals recorded</p>
                  <button
                    onClick={() => {
                      setEditedVitals({});
                      setIsEditingVitals(true);
                    }}
                    className="bg-[#2D869C] text-white px-4 py-2 rounded-lg hover:bg-[#2D869C]/90 transition-colors text-sm font-semibold shadow-sm"
                  >
                    Add Vitals
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Patient History Modal */}
      <PatientHistoryModal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        patient={patient}
      />
    </div>
  );
};

export default CollapsiblePatientCard;